import React, { useContext, useEffect } from 'react'
import { Box } from '@mui/material'
import { Button, Chip, Container, makeStyles, Paper, Typography } from '@material-ui/core'
import getTableOptions from 'utils/getTableOptions'
import Wrapper from 'components/Wrapper'
import Info from 'components/Info'
import BasicTable from 'components/BasicTable'
import ResourceNotFound from 'components/ResourceNotFound'
import { fetchConferenceById } from 'store/conference'
import { EventContext, SessionContext } from 'context'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { EventTypes, PollStatus } from 'constants/index'
import talkSlice, { fetchEventTalksAlongPolls, fetchSessionTalksAlongPolls } from 'store/talk'
import { fetchPollAlongSpeaker } from 'store/polling'
import eventSlice, { fetchEventById, fetchRealTimeEvent } from 'store/event'
import { getPollStatusText } from 'utils/polling'
import { PresentToAll } from '@material-ui/icons'
import { toast } from 'react-toastify'

const EventPolls = () => {
  const classes = useStyles()
  const { event, login, logout } = useContext(EventContext)
  const { session } = useContext(SessionContext)
  const dispatch = useDispatch()
  const history = useHistory()
  const { conference } = useSelector((state) => state.conference)
  const { talks: groupedPolls, inProgress: talkProgress } = useSelector((state) => state.talk)
  const { event: sessionEvent } = useSelector((state) => state.event)

  useEffect(() => {
    const unsub = fetchRealTimeEvent(event?.id, (data) => {
      if (!data) {
        logout()
        dispatch(talkSlice.actions.setTalks([]))
      } else login(data)
    })
    return () => unsub && unsub()
  }, [event?.id])

  useEffect(() => {
    const getEventPoll = async () => {
      dispatch(talkSlice.actions.setProgress(true))
      const poll = event?.havePolls && (await fetchPollAlongSpeaker(event?.pollId))
      const updatedEvent = {
        ...event,
        type: 'Event Poll',
        pollId: poll?.id,
        pollTitle: poll?.title,
        pollPresenter: poll?.presenter,
        pollStatus: poll?.status
      }
      dispatch(talkSlice.actions.setTalks([updatedEvent]))
      dispatch(talkSlice.actions.setProgress(false))
      login(updatedEvent)
    }
    if (event?.id && event?.eventType === EventTypes.STANDALONE) {
      getEventPoll()
    }
  }, [event?.id])

  useEffect(() => {
    const confId = session?.conferenceId ?? event?.conferenceId
    dispatch(fetchConferenceById(confId))
    if (event?.id && event?.eventType === EventTypes.HAVE_TALKS) {
      dispatch(fetchEventTalksAlongPolls(event?.id))
    } else if (session?.id) {
      dispatch(fetchSessionTalksAlongPolls(session?.id))
      dispatch(fetchEventById(session.eventId))
    }
    return () => {
      dispatch(eventSlice.actions.setEvent(null))
    }
  }, [dispatch, event?.id, session?.id])

  if (!event && !session) {
    history.push('/poll')
  }

  const tableHead = (isSession) => {
    let title = ''
    if (session?.id && !event?.id) {
      title = sessionEvent?.isPosterSession ? 'Poster #' : 'Abstract #'
    } else if (event?.id && event.eventType === EventTypes.HAVE_TALKS) {
      title = event?.isPosterSession ? 'Poster #' : 'Abstract #'
    }
    const headings = [
      `${
        isSession
          ? 'Session/Talk Title'
          : event?.eventType === EventTypes.STANDALONE
          ? 'Event Title'
          : 'Talk Title'
      }`,
      'Type',
      'Poll Title',
      'Presenter Name',
      'Poll Status',
      {
        name: 'Actions',
        options: {
          filter: false,
          sort: false
        }
      }
    ]
    if (event?.eventType !== EventTypes.STANDALONE) {
      headings.unshift(title)
    }
    return headings
  }

  const handleNavigatePoll = (pollId, mode) => {
    const isPollInProgress = groupedPolls
      ?.filter((poll) => poll?.pollId !== pollId)
      ?.find((poll) =>
        [PollStatus.STARTED, PollStatus.PAUSED, PollStatus.RESUMED].includes(poll.pollStatus)
      )
    if (isPollInProgress?.pollId) {
      toast.warning(
        `There's already an active poll in this ${
          session?.id ? 'session' : 'event'
        }. Please finish that poll before starting another one.`
      )
      return
    }
    history.push(`/dashboard/polls/${pollId}?mode=${mode}`)
  }

  const handlePresentPoll = () => {
    let url = '/poll-presentation'
    if (session?.id) {
      url = `${url}?sessionId=${session?.id}`
    } else if (event?.id && event.eventType === EventTypes.HAVE_TALKS) {
      url = `${url}?eventId=${event?.id}`
    } else if (event.havePolls) {
      url = `${url}?pollId=${event?.pollId}`
    }
    history.push(url)
  }

  const formatTableData = (groupedPolls) =>
    groupedPolls?.map((groupedPoll) => {
      const { status, bgColor, color } = getPollStatusText(groupedPoll?.pollStatus)
      let abs_pos_no = ''
      if (groupedPoll?.isAbstract && groupedPoll?.posterNumber) {
        abs_pos_no = `${conference?.posterPrefix} - ${groupedPoll?.posterNumber}`
      } else if (groupedPoll?.isAbstract && groupedPoll?.abstractNumber) {
        abs_pos_no = `${conference?.abstractPrefix} - ${groupedPoll?.abstractNumber}`
      }
      const rows = [
        groupedPoll.title,
        groupedPoll?.type,
        groupedPoll?.pollTitle,
        groupedPoll?.pollPresenter,
        <Chip
          label={status}
          size="small"
          style={{
            backgroundColor: bgColor,
            color,
            width: '100px',
            letterSpacing: '0.5px',
            padding: '14px 0px',
            fontWeight: 'bold'
          }}
        />,
        groupedPoll?.pollStatus !== PollStatus.CLOSED && (
          <Button
            size="large"
            className={classes.startBtn}
            variant="contained"
            onClick={() => handleNavigatePoll(groupedPoll?.pollId, 'view')}>
            Start Poll
          </Button>
        )
      ]
      if (event?.eventType !== EventTypes.STANDALONE) {
        rows.unshift(abs_pos_no)
      }
      return rows
    })

  const options = getTableOptions()
  return (
    <Container className={classes.container}>
      <Wrapper inProgress={talkProgress}>
        <Info
          eventTitle={event?.title}
          eventStartDate={event?.startDate}
          eventEndDate={event?.endDate}
          sessionTitle={session?.title}
          sessionStartDate={session?.startDate}
          sessionEndDate={session?.endDate}
          timezone={conference?.timezone}
        />
        <Box className={classes.header}>
          <Typography variant="h5">{event?.id ? 'Event ' : 'Session '}Polls</Typography>
          {groupedPolls?.length ? (
            <Box className={classes.splash}>
              <Typography className={classes.info}>
                Click the &quot;Splash page&quot; button to show a temporary screen until actual
                poll starts.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PresentToAll />}
                onClick={handlePresentPoll}
                className={classes.splashButton}>
                Splash Page
              </Button>
            </Box>
          ) : null}
        </Box>
        <Paper variant="outlined" style={{ marginTop: '16px' }}>
          {groupedPolls?.length ? (
            <BasicTable
              data={formatTableData(groupedPolls)}
              columns={tableHead(!!session?.id)}
              options={options}
            />
          ) : (
            <ResourceNotFound message={getNotFoundMessage(event?.eventType, !!session?.id)} />
          )}
        </Paper>
      </Wrapper>
    </Container>
  )
}
export default EventPolls

export const getNotFoundMessage = (eventType, isSession) => {
  if (isSession) {
    return `No session/talk available that contains any poll.`
  }

  return eventType === EventTypes.STANDALONE
    ? 'No poll associated with this event'
    : 'No talk available that contains any poll.'
}

const useStyles = makeStyles({
  container: {
    paddingTop: '7.75rem'
  },
  startBtn: {
    backgroundColor: '#009b00',
    color: '#FFFFFF',
    fontWeight: 'bold',
    textTransform: 'none',
    fontSize: '18px',
    letterSpacing: '0.5px',
    width: '110px',
    padding: '8px',
    '&:hover': {
      backgroundColor: '#007300'
    }
  },
  header: {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'end'
  },
  splash: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end',
    maxWidth: '313px',
    gap: '10px'
  },
  info: {
    fontSize: '16px',
    color: '#545454',
    fontWeight: 'bold'
  },
  splashButton: {
    textTransform: 'none',
    letterSpacing: '0.5px',
    fontSize: '20px',
    fontWeight: 'bold',
    padding: '14px 24px'
  }
})
